import type { AxiosResponse } from 'axios'

export interface CreditHeaders {
  'X-Credit-Balance'?: string
  'X-Credit-Deducted'?: string
  'X-Credit-TotalSpent'?: string
  'X-Credit-Type'?: string
  'X-Credit-WalletId'?: string
  'X-Process-Time'?: string
  'X-Request-Id'?: string
}

export interface ParsedCreditData {
  creditBalance?: number
  creditDeducted?: number
  creditTotalSpent?: number
  creditType?: string
  creditWalletId?: number
  processTime?: number
  requestId?: string
}

/**
 * Extract credit information from response headers
 * @param response - Axios response object
 * @returns Parsed credit data object
 */
export function extractCreditFromHeaders(response: AxiosResponse): ParsedCreditData {
  const headers = response.headers
  const creditHeaders: CreditHeaders = {
    'X-Credit-Balance': headers['x-credit-balance'] || headers['X-Credit-Balance'],
    'X-Credit-Deducted': headers['x-credit-deducted'] || headers['X-Credit-Deducted'],
    'X-Credit-TotalSpent': headers['x-credit-totalspent'] || headers['X-Credit-TotalSpent'],
    'X-Credit-Type': headers['x-credit-type'] || headers['X-Credit-Type'],
    'X-Credit-WalletId': headers['x-credit-walletid'] || headers['X-Credit-WalletId'],
    'X-Process-Time': headers['x-process-time'] || headers['X-Process-Time'],
    'X-Request-Id': headers['x-request-id'] || headers['X-Request-Id'],
  }

  const parsedData: ParsedCreditData = {}

  if (creditHeaders['X-Credit-Balance']) {
    const creditBalance = parseFloat(creditHeaders['X-Credit-Balance'])
    if (!isNaN(creditBalance))
      parsedData.creditBalance = creditBalance
  }

  if (creditHeaders['X-Credit-Deducted']) {
    const creditDeducted = parseFloat(creditHeaders['X-Credit-Deducted'])
    if (!isNaN(creditDeducted))
      parsedData.creditDeducted = creditDeducted
  }

  if (creditHeaders['X-Credit-TotalSpent']) {
    const creditTotalSpent = parseFloat(creditHeaders['X-Credit-TotalSpent'])
    if (!isNaN(creditTotalSpent))
      parsedData.creditTotalSpent = creditTotalSpent
  }

  if (creditHeaders['X-Credit-Type'])
    parsedData.creditType = creditHeaders['X-Credit-Type']

  if (creditHeaders['X-Credit-WalletId']) {
    const creditWalletId = parseInt(creditHeaders['X-Credit-WalletId'])
    if (!isNaN(creditWalletId))
      parsedData.creditWalletId = creditWalletId
  }

  if (creditHeaders['X-Process-Time']) {
    const processTime = parseFloat(creditHeaders['X-Process-Time'])
    if (!isNaN(processTime))
      parsedData.processTime = processTime
  }

  if (creditHeaders['X-Request-Id'])
    parsedData.requestId = creditHeaders['X-Request-Id']

  return parsedData
}

/**
 * Check if response contains credit headers
 * @param response - Axios response object
 * @returns boolean indicating if credit headers are present
 */
export function hasCreditHeaders(response: AxiosResponse): boolean {
  const headers = response.headers
  const creditHeaders = [
    'x-credit-balance',
    'x-credit-deducted',
    'x-credit-totalspent',
    'x-credit-type',
    'x-credit-walletid',
    'x-process-time',
    'x-request-id',
  ]

  // Check both lowercase and uppercase versions
  return creditHeaders.some((header) => {
    const lowerHeader = header.toLowerCase()
    const upperHeader = header.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
    return headers[lowerHeader] !== undefined || headers[upperHeader] !== undefined
  })
}

/**
 * Update credit store with data from response headers
 * @param response - Axios response object
 */
export function updateCreditFromHeaders(response: AxiosResponse): void {
  if (!hasCreditHeaders(response))
    return

  const creditData = extractCreditFromHeaders(response)

  // Dynamically import and update credit store
  import('@/store').then(({ useCreditStore }) => {
    try {
      const creditStore = useCreditStore()

      // Update credit balance if available
      if (creditData.creditBalance !== undefined) {
        // Update credit store based on credit type
        if (creditData.creditType === 'advance_credit')
          // Update advanced credit balance
          creditStore.updateCreditBalances(creditStore.basicCreditBalance, creditData.creditBalance)
        else
          // Update basic credit balance
          creditStore.updateCreditBalances(creditData.creditBalance, creditStore.advancedCreditBalance)
      }
    }
    catch (error) {
      console.warn('Failed to update credit from headers:', error)
    }
  }).catch(() => {
    // Silently handle import errors
  })
}
